import { NOVA_TILT_RANGES } from 'projectSections/sections/design/systems/tabs/mounting/nativeMounting/mounting_systems/Nova/NovaTiltRanges'
import { resolveVanDerValkRackingTypeFromRow } from 'projectSections/sections/design/systems/tabs/mounting/vandervalk/applyVanDerValkSuitabilityResultToSystem'
import type { VanDerValkSuitabilityConfig } from 'projectSections/sections/design/systems/tabs/mounting/vandervalk/types'
import type { MeasurementUnits } from 'types/orgs'
import type { StudioSystemType } from 'types/studio/items'
import { parseIntegrationJson } from 'util/misc'
import MODULE_GRID_SETTER_GETTER_CONFIGS from './accessors/configs'

export type MountingFieldOverrides = {
  rackingEditable: boolean
  slopeEditable: boolean
  slopeReEnableAllowed: boolean
  relativeTiltEditable: boolean
  relativeTiltMin?: number
  relativeTiltMax?: number
  relativeTiltTooltip: string | null
  landscapeAllowed: boolean
  groundClearanceDisabled: boolean
  groundClearanceTooltip: string | null
  groundClearanceUnit: string
  groundClearanceStep: number
  groundClearanceMaxDecimals: number
  parseGroundClearanceForDisplay: (value: number | undefined) => number | undefined
  formatGroundClearanceForSave: (displayValue: number) => number | null
  moduleSpacingDisabled: boolean
  moduleSpacingTooltip: string | null
  panelOrientationDisabled: boolean
  panelOrientationTooltip: string | null
  modulesPerColDisabled: boolean
  modulesPerColTooltip: string | null
  modulesPerRowDisabled: boolean
  modulesPerRowTooltip: string | null
}

const gcAccessor = MODULE_GRID_SETTER_GETTER_CONFIGS['groundClearance']

function defaultOverrides(measurements: MeasurementUnits): MountingFieldOverrides {
  const isImperial = measurements === 'imperial'
  return {
    rackingEditable: true,
    slopeEditable: true,
    slopeReEnableAllowed: true,
    relativeTiltEditable: true,
    relativeTiltMin: undefined,
    relativeTiltMax: undefined,
    relativeTiltTooltip: null,
    landscapeAllowed: true,
    groundClearanceDisabled: false,
    groundClearanceTooltip: null,
    groundClearanceUnit: isImperial ? 'in' : 'm',
    groundClearanceStep: isImperial ? 0.5 : 0.1,
    groundClearanceMaxDecimals: 4,
    parseGroundClearanceForDisplay: (value) => gcAccessor.parse(value, { measurements }),
    formatGroundClearanceForSave: (displayValue) => gcAccessor.format(displayValue, { measurements }) ?? null,
    moduleSpacingDisabled: false,
    moduleSpacingTooltip: null,
    panelOrientationDisabled: false,
    panelOrientationTooltip: null,
    modulesPerColDisabled: false,
    modulesPerColTooltip: null,
    modulesPerRowDisabled: false,
    modulesPerRowTooltip: null,
  }
}

/** i18n keys — set on disabled panel fields driven by suitability selection. */
const VDV_GROUND_CLEARANCE_TOOLTIP = 'mount_vdv_ground_clearance_from_suitability'
const VDV_MODULE_SPACING_TOOLTIP = 'mount_vdv_module_spacing_from_suitability'
const VDV_PANEL_ORIENTATION_TOOLTIP = 'mount_vdv_panel_orientation_from_suitability'
const VDV_RELATIVE_TILT_TOOLTIP = 'mount_vdv_relative_tilt_from_suitability'
const VDV_MODULES_PER_AXIS_TOOLTIP = 'mount_vdv_modules_per_axis_from_suitability'

function solarPortOverrides(measurements: MeasurementUnits): MountingFieldOverrides {
  const isImperial = measurements === 'imperial'

  if (isImperial) {
    return {
      ...defaultOverrides(measurements),
      rackingEditable: false,
      slopeEditable: false,
      slopeReEnableAllowed: false,
      relativeTiltEditable: false,
      landscapeAllowed: false,
    }
  }

  return {
    ...defaultOverrides(measurements),
    rackingEditable: false,
    slopeEditable: false,
    slopeReEnableAllowed: false,
    relativeTiltEditable: false,
    landscapeAllowed: false,
    groundClearanceDisabled: false,
    groundClearanceTooltip: null,
    groundClearanceUnit: 'mm',
    groundClearanceStep: 1,
    groundClearanceMaxDecimals: 0,
    parseGroundClearanceForDisplay: (value) => (value !== undefined ? Math.round(value * 1000) : undefined),
    formatGroundClearanceForSave: (displayValue) => displayValue / 1000,
  }
}

function solarNestOverrides(mounting: string, measurements: MeasurementUnits): MountingFieldOverrides {
  const isGroundMount = mounting === 'solarNestGroundMount'
  return {
    ...defaultOverrides(measurements),
    rackingEditable: false,
    groundClearanceDisabled: isGroundMount,
    groundClearanceTooltip: isGroundMount ? 'Ground clearance is controlled by the mounting system settings.' : null,
  }
}

export function getVanDerValkSelectedSuitabilityRow(
  system: StudioSystemType | undefined
): VanDerValkSuitabilityConfig | null {
  if (!system?.integration_json) {
    return null
  }
  const selected = (
    parseIntegrationJson(system.integration_json) as {
      vanDerValk?: { selectedSuitability?: VanDerValkSuitabilityConfig | null }
    } | null
  )?.vanDerValk?.selectedSuitability
  return selected ?? null
}

function hasVanDerValkSuitabilitySelection(system: StudioSystemType | undefined): boolean {
  return getVanDerValkSelectedSuitabilityRow(system) != null
}

function vanDerValkOverrides(measurements: MeasurementUnits, system?: StudioSystemType): MountingFieldOverrides {
  const selectedRow = getVanDerValkSelectedSuitabilityRow(system)
  const isSingleTilt = selectedRow != null && resolveVanDerValkRackingTypeFromRow(selectedRow) === 'TILT_RACK'

  return {
    ...defaultOverrides(measurements),
    rackingEditable: !hasVanDerValkSuitabilitySelection(system),
    relativeTiltEditable: false,
    relativeTiltTooltip: VDV_RELATIVE_TILT_TOOLTIP,
    groundClearanceDisabled: true,
    groundClearanceTooltip: VDV_GROUND_CLEARANCE_TOOLTIP,
    moduleSpacingDisabled: true,
    moduleSpacingTooltip: VDV_MODULE_SPACING_TOOLTIP,
    panelOrientationDisabled: true,
    panelOrientationTooltip: VDV_PANEL_ORIENTATION_TOOLTIP,
    modulesPerColDisabled: isSingleTilt,
    modulesPerColTooltip: isSingleTilt ? VDV_MODULES_PER_AXIS_TOOLTIP : null,
    modulesPerRowDisabled: isSingleTilt,
    modulesPerRowTooltip: isSingleTilt ? VDV_MODULES_PER_AXIS_TOOLTIP : null,
  }
}

function fastRackOverrides(measurements: MeasurementUnits): MountingFieldOverrides {
  return {
    ...defaultOverrides(measurements),
    rackingEditable: false,
    relativeTiltEditable: false,
  }
}

function novaOverrides(
  measurements: MeasurementUnits,
  system?: { integration_json?: Record<string, any> }
): MountingFieldOverrides {
  const novaJson = system?.integration_json?.nova ?? window.editor?.selectedSystem?.integration_json?.nova
  const tiltOption = String(novaJson?.tiltOption || 'No tilt')
  const range = NOVA_TILT_RANGES[tiltOption] || NOVA_TILT_RANGES['No tilt']
  return {
    ...defaultOverrides(measurements),
    rackingEditable: false,
    relativeTiltMin: range.min,
    relativeTiltMax: range.max,

  }
}

export function getMountingFieldOverrides(
  mounting: string | undefined,
  measurements: MeasurementUnits,
  system?: StudioSystemType
): MountingFieldOverrides {
  if (mounting === 'solarPortGroundMount') {
    return solarPortOverrides(measurements)
  }
  if (mounting === 'solarNestRooftop' || mounting === 'solarNestGroundMount') {
    return solarNestOverrides(mounting, measurements)
  }
  if (mounting === 'fastRack') {
    return fastRackOverrides(measurements)
  }
  if (mounting === 'nova') {
    return novaOverrides(measurements, system)
  }
  if (mounting === 'vanDerValk') {
    return vanDerValkOverrides(measurements, system)
  }
  return defaultOverrides(measurements)
}
